import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch, type Control, type FieldErrors, type UseFormClearErrors, type UseFormSetValue } from 'react-hook-form';
import { Select } from '../../../components/common';
import { FormField } from '../../../components/form/FormField.tsx';
import { PaginatedSelectMenuList } from '../../risks/components/PaginatedSelectMenuList.tsx';
import { useControlItgcDomainOptions } from '../../../hooks/controls/useControlItgcDomainOptions.ts';
import { useControlSystemOptions } from '../../../hooks/controls/useControlSystemOptions.ts';
import type { ControlFormValues } from '../schema.ts';

const ITGC_PICKER_SELECT_STYLES = {
  control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
    ...base,
    minHeight: 42,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    backgroundColor: '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
    fontSize: '0.875rem',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
    },
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 40,
    padding: '2px 12px',
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    color: '#6b7280',
    padding: '0 8px',
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    maxHeight: 280,
    overflowY: 'auto',
  }),
};

const ITGC_PICKER_COMPONENTS = {
  MenuList: PaginatedSelectMenuList,
};

type SystemPickerOption = { value: string; label: string };

function mergeSelectedSystemOptions(
  searchOptions: SystemPickerOption[],
  selectedIds: string[],
  selectedOptionById: Record<string, SystemPickerOption>
): SystemPickerOption[] {
  const merged = new Map<string, SystemPickerOption>();
  selectedIds.forEach((id) => {
    const cached = selectedOptionById[id];
    if (cached) merged.set(id, cached);
  });
  searchOptions.forEach((option) => {
    merged.set(option.value, option);
  });
  return Array.from(merged.values());
}

function mergeSelectedPickerOption(
  searchOptions: SystemPickerOption[],
  selectedId: string,
  selectedOption: SystemPickerOption | null,
  searchInput: string
): SystemPickerOption[] {
  // While searching, show only API search matches. Keep selected cached option only when
  // search is empty so the closed/idle field can still resolve and display the label.
  if (searchInput.trim()) return searchOptions;
  const merged = new Map<string, SystemPickerOption>();
  if (selectedId && selectedOption) merged.set(selectedOption.value, selectedOption);
  searchOptions.forEach((option) => {
    merged.set(option.value, option);
  });
  return Array.from(merged.values());
}

type ControlItgcCreateFieldsProps = {
  enabled: boolean;
  readOnly?: boolean;
  formControl: Control<ControlFormValues>;
  errors: FieldErrors<ControlFormValues>;
  clearErrors?: UseFormClearErrors<ControlFormValues>;
  setValue: UseFormSetValue<ControlFormValues>;
};

export const ControlItgcCreateFields: React.FC<ControlItgcCreateFieldsProps> = ({
  enabled,
  readOnly = false,
  formControl,
  errors,
  clearErrors,
  setValue,
}) => {
  const [systemSearchInput, setSystemSearchInput] = useState('');
  const [domainSearchInput, setDomainSearchInput] = useState('');
  const [selectedSystemOptionById, setSelectedSystemOptionById] = useState<
    Record<string, SystemPickerOption>
  >({});
  const [selectedDomainOption, setSelectedDomainOption] = useState<SystemPickerOption | null>(null);

  const selectedSystemIds = useWatch({ control: formControl, name: 'itgcSystemIds' });
  const selectedDomainId = useWatch({ control: formControl, name: 'itgcDomainId' });
  const normalizedSelectedSystemIds = Array.isArray(selectedSystemIds) ? selectedSystemIds : [];
  const normalizedSelectedDomainId = typeof selectedDomainId === 'string' ? selectedDomainId.trim() : '';

  const systemOptionsQuery = useControlSystemOptions(systemSearchInput, enabled);
  const domainOptionsQuery = useControlItgcDomainOptions(domainSearchInput, enabled);

  const systemOptions = useMemo(
    () => (systemOptionsQuery.data?.pages ?? []).flatMap((page) => page.items),
    [systemOptionsQuery.data?.pages]
  );

  const systemOptionsForSelect = useMemo(
    () => mergeSelectedSystemOptions(systemOptions, normalizedSelectedSystemIds, selectedSystemOptionById),
    [normalizedSelectedSystemIds, selectedSystemOptionById, systemOptions]
  );

  const domainOptions = useMemo(
    () => (domainOptionsQuery.data?.pages ?? []).flatMap((page) => page.items),
    [domainOptionsQuery.data?.pages]
  );

  const domainOptionsForSelect = useMemo(
    () =>
      mergeSelectedPickerOption(
        domainOptions,
        normalizedSelectedDomainId,
        selectedDomainOption,
        domainSearchInput
      ),
    [domainOptions, domainSearchInput, normalizedSelectedDomainId, selectedDomainOption]
  );

  useEffect(() => {
    if (!enabled) {
      setValue('itgcSystemIds', [], { shouldDirty: true, shouldValidate: true });
      setValue('itgcDomainId', '', { shouldDirty: true, shouldValidate: true });
      setSystemSearchInput('');
      setDomainSearchInput('');
      setSelectedSystemOptionById({});
      setSelectedDomainOption(null);
    }
  }, [enabled, setValue]);

  useEffect(() => {
    if (!normalizedSelectedDomainId) {
      setSelectedDomainOption(null);
      return;
    }
    const match = domainOptions.find((option) => option.value === normalizedSelectedDomainId);
    if (match) {
      setSelectedDomainOption((prev) =>
        prev?.value === match.value && prev.label === match.label ? prev : match
      );
    }
  }, [domainOptions, normalizedSelectedDomainId]);

  useEffect(() => {
    if (!systemOptions.length || !normalizedSelectedSystemIds.length) return;
    setSelectedSystemOptionById((prev) => {
      let changed = false;
      const next = { ...prev };
      systemOptions.forEach((option) => {
        if (!normalizedSelectedSystemIds.includes(option.value)) return;
        if (!next[option.value] || next[option.value].label !== option.label) {
          next[option.value] = option;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [normalizedSelectedSystemIds, systemOptions]);

  if (!enabled) return null;

  const systemSelectProps = {
    loadingMoreOptions: systemOptionsQuery.isFetchingNextPage,
    hasMoreOptions: Boolean(systemOptionsQuery.hasNextPage),
    onOptionsNearEnd: () => {
      if (systemOptionsQuery.hasNextPage && !systemOptionsQuery.isFetchingNextPage) {
        void systemOptionsQuery.fetchNextPage();
      }
    },
  };

  const domainSelectProps = {
    loadingMoreOptions: domainOptionsQuery.isFetchingNextPage,
    hasMoreOptions: Boolean(domainOptionsQuery.hasNextPage),
    onOptionsNearEnd: () => {
      if (domainOptionsQuery.hasNextPage && !domainOptionsQuery.isFetchingNextPage) {
        void domainOptionsQuery.fetchNextPage();
      }
    },
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 pt-4 border-t border-gray-200">
      <Controller
        name="itgcSystemIds"
        control={formControl}
        render={({ field }) => {
          const selectedValues = Array.isArray(field.value) ? field.value : [];
          const systemError = errors.itgcSystemIds?.message as string | undefined;
          return (
            <FormField label="Systems" required error={systemError} htmlFor="itgcSystemIds">
              <Select
                inputId="itgcSystemIds"
                aria-label="Systems"
                className={systemError ? 'ring-1 ring-red-500 rounded-lg' : 'w-full'}
                options={systemOptionsForSelect}
                value={selectedValues}
                onChange={(value) => {
                  const nextValues = Array.isArray(value) ? value : [];
                  clearErrors?.('itgcSystemIds');
                  field.onChange(nextValues);
                  setSelectedSystemOptionById((prev) => {
                    const next: Record<string, SystemPickerOption> = {};
                    nextValues.forEach((id) => {
                      const fromSearch = systemOptions.find((option) => option.value === id);
                      next[id] = fromSearch ?? prev[id] ?? { value: id, label: id };
                    });
                    return next;
                  });
                  setSystemSearchInput('');
                }}
                onBlur={field.onBlur}
                disabled={readOnly}
                isMulti
                isSearchable
                isLoading={systemOptionsQuery.isLoading}
                inputValue={systemSearchInput}
                onInputChange={(value, meta) => {
                  if (meta.action === 'input-change') {
                    setSystemSearchInput(value);
                    return;
                  }
                  if (
                    meta.action === 'set-value' ||
                    meta.action === 'menu-close' ||
                    meta.action === 'input-blur'
                  ) {
                    setSystemSearchInput('');
                  }
                }}
                filterOption={() => true}
                components={ITGC_PICKER_COMPONENTS}
                styles={ITGC_PICKER_SELECT_STYLES}
                {...systemSelectProps}
                placeholder="Select systems..."
                placeholderOnFocus="Search systems..."
                noOptionsMessage={() =>
                  systemOptionsQuery.isLoading ? 'Loading systems...' : 'No systems found'
                }
                menuPlacement="auto"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition="absolute"
              />
            </FormField>
          );
        }}
      />

      <Controller
        name="itgcDomainId"
        control={formControl}
        render={({ field }) => {
          const domainError = errors.itgcDomainId?.message as string | undefined;
          return (
            <FormField label="Domain" required error={domainError} htmlFor="itgcDomainId">
              <Select
                inputId="itgcDomainId"
                aria-label="ITGC domain"
                className={domainError ? 'ring-1 ring-red-500 rounded-lg' : 'w-full'}
                options={domainOptionsForSelect}
                value={field.value ?? ''}
                onChange={(value) => {
                  const nextValue = typeof value === 'string' ? value : '';
                  clearErrors?.('itgcDomainId');
                  field.onChange(nextValue);
                  const fromSearch = domainOptions.find((option) => option.value === nextValue);
                  setSelectedDomainOption(
                    nextValue ? fromSearch ?? { value: nextValue, label: nextValue } : null
                  );
                  setDomainSearchInput('');
                }}
                onBlur={field.onBlur}
                disabled={readOnly}
                isSearchable
                isLoading={domainOptionsQuery.isLoading}
                inputValue={domainSearchInput}
                onInputChange={(value, meta) => {
                  if (meta.action === 'input-change') {
                    setDomainSearchInput(value);
                    return;
                  }
                  if (
                    meta.action === 'set-value' ||
                    meta.action === 'menu-close' ||
                    meta.action === 'input-blur'
                  ) {
                    setDomainSearchInput('');
                  }
                }}
                filterOption={() => true}
                components={ITGC_PICKER_COMPONENTS}
                styles={ITGC_PICKER_SELECT_STYLES}
                {...domainSelectProps}
                placeholder="Select domain..."
                placeholderOnFocus="Search domains..."
                noOptionsMessage={() =>
                  domainOptionsQuery.isLoading ? 'Loading domains...' : 'No domains found'
                }
                menuPlacement="auto"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition="absolute"
              />
            </FormField>
          );
        }}
      />
    </div>
  );
};

export default ControlItgcCreateFields;
